import React, { useCallback, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { sheetsAPI, formatarErroAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  FileSpreadsheet, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Clock, ExternalLink, PlugZap,
  CloudUpload, RotateCcw, ShieldCheck, Trash2, ChevronDown, ListChecks,
} from 'lucide-react';

const fmtDataHora = (iso) => (iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-');

const StatCard = ({ titulo, valor, icone: Icone, cor, testid, sub }) => (
  <Card data-testid={testid}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${cor}`}><Icone className="w-5 h-5 text-white" /></div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-gray-400">{titulo}</p>
        <p className="text-2xl font-bold leading-tight">{valor}</p>
        {sub && <p className="text-[11px] text-gray-400 truncate">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

const Passos = () => {
  const [aberto, setAberto] = useState(false);
  return (
    <div className="rounded-lg border border-dashed p-3 text-sm" data-testid="sheets-passos">
      <button onClick={() => setAberto(!aberto)} className="w-full flex items-center justify-between font-medium text-left" data-testid="sheets-passos-toggle">
        <span>Como obter o JSON da Conta de Serviço (≈5 min)</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${aberto ? 'rotate-180' : ''}`} />
      </button>
      {aberto && (
        <ol className="list-decimal ml-5 mt-3 space-y-1.5 text-gray-600 dark:text-gray-300">
          <li>Acesse <a className="text-purple-600 underline" href="https://console.cloud.google.com" target="_blank" rel="noreferrer">console.cloud.google.com</a> e crie (ou selecione) um projeto.</li>
          <li>Em <b>APIs e serviços → Biblioteca</b>, procure <b>Google Sheets API</b> e clique em <b>Ativar</b>.</li>
          <li>Em <b>IAM e administrador → Contas de serviço → Criar conta de serviço</b> (nome ex.: <i>crm-sheets</i>), conclua sem papéis.</li>
          <li>Abra a conta criada → aba <b>Chaves → Adicionar chave → Criar nova chave → JSON</b>. O arquivo baixa automaticamente.</li>
          <li>Crie a planilha no Google Drive e clique em <b>Compartilhar</b>: cole o e-mail da conta de serviço (termina em <i>iam.gserviceaccount.com</i>) com permissão <b>Editor</b>.</li>
          <li>Cole abaixo o conteúdo do JSON e o link da planilha e clique em <b>Salvar e testar</b>. As abas "Vendas" e "Histórico" são criadas automaticamente.</li>
        </ol>
      )}
    </div>
  );
};

const GoogleSheets = () => {
  const { user } = useAuth();
  const isAdmin = user?.perfil === 'ADMIN';
  const [status, setStatus] = useState(null);
  const [fila, setFila] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [acao, setAcao] = useState(null);
  const [credJson, setCredJson] = useState('');
  const [planilha, setPlanilha] = useState('');

  const carregar = useCallback(async (silencioso = false) => {
    try {
      const [s, f] = await Promise.all([sheetsAPI.status(), sheetsAPI.fila({ limite: 30 })]);
      setStatus(s.data);
      setFila(f.data);
    } catch (e) {
      if (!silencioso) toast.error('Erro ao carregar status do Google Sheets');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
    const id = setInterval(() => { if (!document.hidden) carregar(true); }, 15000);
    return () => clearInterval(id);
  }, [carregar]);

  const executar = async (nome, fn, okMsg) => {
    setAcao(nome);
    try {
      const r = await fn();
      toast.success(okMsg ? okMsg(r.data) : r.data?.message || 'Concluído');
      await carregar(true);
      return r.data;
    } catch (e) {
      toast.error(formatarErroAPI(e));
    } finally {
      setAcao(null);
    }
  };

  const salvar = () => executar('salvar', () => sheetsAPI.salvarConfig({ credentials_json: credJson, spreadsheet_id: planilha }),
    (d) => `Conectado à planilha "${d.planilha_titulo}"`).then((d) => { if (d) { setCredJson(''); setPlanilha(''); } });

  if (!isAdmin) return <Navigate to="/dashboard" replace />;
  if (carregando) return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-purple-600" /></div>;

  const conectado = status?.configurado;

  return (
    <div className="space-y-6" data-testid="google-sheets-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileSpreadsheet className="w-7 h-7 text-emerald-600" /> Google Sheets</h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Sincronização automática das vendas com a planilha no Google Drive — 1 linha por venda, atualizada sem duplicidade, com histórico de cada alteração</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => executar('testar', sheetsAPI.testar, (d) => `Conexão OK — planilha "${d.planilha_titulo}"`)} disabled={!conectado || !!acao} data-testid="sheets-testar-btn">
            {acao === 'testar' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlugZap className="w-4 h-4 mr-1" />} Testar conexão
          </Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => executar('agora', sheetsAPI.sincronizarAgora, (d) => `${d.processados} item(ns) enviados · ${d.pendentes} pendente(s)`)} disabled={!conectado || !!acao} data-testid="sheets-sincronizar-agora-btn">
            {acao === 'agora' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Sincronizar agora
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Card data-testid="sheets-conexao-card" className={conectado ? 'border-emerald-300 dark:border-emerald-800' : 'border-amber-300 dark:border-amber-800'}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-500 dark:text-gray-400">Conexão</p>
              {conectado ? <Badge className="bg-emerald-100 text-emerald-700" data-testid="sheets-conexao-badge">Conectado</Badge> : <Badge className="bg-amber-100 text-amber-700" data-testid="sheets-conexao-badge">Não configurado</Badge>}
            </div>
            <p className="text-sm font-semibold mt-2 truncate" title={status?.planilha_titulo || ''}>{status?.planilha_titulo || (conectado ? 'Planilha' : 'Aguardando credenciais')}</p>
            {status?.spreadsheet_url && (
              <a href={status.spreadsheet_url} target="_blank" rel="noreferrer" className="text-xs text-purple-600 inline-flex items-center gap-1 mt-1" data-testid="sheets-abrir-planilha">Abrir planilha <ExternalLink className="w-3 h-3" /></a>
            )}
            {conectado && isAdmin && (
              <div className="flex items-center gap-2 mt-2">
                <Switch checked={!!status?.ativo} onCheckedChange={(v) => executar('ativo', () => sheetsAPI.alternarAtivo(v), () => (v ? 'Sincronização ativada' : 'Sincronização pausada'))} data-testid="sheets-ativo-switch" />
                <span className="text-xs text-gray-500">{status?.ativo ? 'Sincronização ativa' : 'Pausada'}</span>
              </div>
            )}
          </CardContent>
        </Card>
        <StatCard titulo="Pendentes na fila" valor={status?.pendentes ?? 0} icone={Clock} cor="bg-amber-500" testid="sheets-pendentes-card" sub={status?.ultima_sync ? `Último envio ${fmtDataHora(status.ultima_sync)}` : 'Nenhum envio ainda'} />
        <StatCard titulo="Sincronizadas (24h)" valor={status?.sincronizados_24h ?? 0} icone={CheckCircle2} cor="bg-emerald-500" testid="sheets-ok-card" sub={`${status?.sincronizados ?? 0} no total`} />
        <StatCard titulo="Com erro" valor={status?.erros ?? 0} icone={AlertTriangle} cor={status?.erros ? 'bg-red-500' : 'bg-gray-400'} testid="sheets-erros-card" sub={status?.ultimo_erro ? `Último: ${fmtDataHora(status.ultimo_erro_em)}` : 'Sem falhas'} />
        <StatCard titulo="Vendas no CRM" valor={status?.total_vendas ?? 0} icone={ListChecks} cor="bg-purple-600" testid="sheets-total-card" sub="Elegíveis à sincronização inicial" />
      </div>

      {status?.ultimo_erro && (
        <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800 p-3 text-sm text-red-700 dark:text-red-300 flex items-start gap-2" data-testid="sheets-ultimo-erro">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div><b>Último erro:</b> {status.ultimo_erro} <span className="text-xs opacity-70">— as tentativas de reenvio são automáticas (1, 2, 5, 15, 30, 60 min…)</span></div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        <Card className="xl:col-span-2" data-testid="sheets-config-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base"><ShieldCheck className="w-5 h-5 text-purple-600" /> Acesso protegido</CardTitle>
            <CardDescription>Conta de Serviço do Google (servidor → planilha). A chave fica só no servidor e nunca é exibida.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {conectado && (
              <div className="rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 p-3 text-sm" data-testid="sheets-conta-atual">
                <p className="text-emerald-800 dark:text-emerald-200 font-medium">Conta de serviço em uso</p>
                <p className="font-mono text-xs break-all mt-1">{status.client_email}</p>
                <p className="text-xs text-gray-500 mt-1">Origem: {status.origem === 'env' ? 'variáveis de ambiente' : 'painel'} · ID {status.spreadsheet_id}</p>
              </div>
            )}
            {isAdmin ? (
              <>
                <Passos />
                <div className="space-y-2">
                  <Label htmlFor="sheets-json">JSON da Conta de Serviço {conectado && <span className="text-xs text-gray-400">(deixe em branco para manter a atual)</span>}</Label>
                  <textarea id="sheets-json" value={credJson} onChange={(e) => setCredJson(e.target.value)} rows={5} placeholder='{ "type": "service_account", "client_email": "...", "private_key": "..." }' className="w-full rounded-md border bg-background px-3 py-2 text-xs font-mono" data-testid="sheets-json-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sheets-planilha">Link ou ID da planilha</Label>
                  <Input id="sheets-planilha" value={planilha} onChange={(e) => setPlanilha(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/XXXX/edit" data-testid="sheets-planilha-input" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button onClick={salvar} disabled={!!acao || (!credJson && !planilha)} className="bg-purple-600 hover:bg-purple-700" data-testid="sheets-salvar-btn">
                    {acao === 'salvar' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-1" />} Salvar e testar
                  </Button>
                  {conectado && status?.origem !== 'env' && (
                    <Button variant="outline" onClick={() => { if (window.confirm('Remover as credenciais? A sincronização para até configurar novamente.')) executar('remover', sheetsAPI.removerConfig); }} disabled={!!acao} data-testid="sheets-remover-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Remover credenciais
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">Somente o ADMIN pode alterar as credenciais.</p>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-3" data-testid="sheets-fila-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="text-base">Fila de sincronização</CardTitle>
                <CardDescription>Pendentes e falhas (reenvio automático). Aba "{status?.abas?.vendas}" = 1 linha por venda · aba "{status?.abas?.historico}" = cada alteração com data/hora.</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => executar('reprocessar', sheetsAPI.reprocessarErros)} disabled={!status?.erros || !!acao} data-testid="sheets-reprocessar-btn">
                  <RotateCcw className="w-4 h-4 mr-1" /> Reprocessar erros
                </Button>
                <Button size="sm" variant="outline" className="border-purple-300 text-purple-700" onClick={() => { if (window.confirm(`Enviar todas as ${status?.total_vendas ?? 0} vendas já cadastradas para a planilha? Linhas existentes são atualizadas, nada é duplicado.`)) executar('tudo', sheetsAPI.sincronizarTudo); }} disabled={!!acao} data-testid="sheets-sincronizar-tudo-btn">
                  {acao === 'tudo' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-1" />} Sincronização inicial (todas)
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {fila.length === 0 ? (
              <div className="text-center py-10 text-gray-500 text-sm" data-testid="sheets-fila-vazia"><CheckCircle2 className="w-8 h-8 mx-auto mb-2 text-emerald-500" /> Nada pendente — tudo sincronizado.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="sheets-fila-tabela">
                  <thead>
                    <tr className="border-b bg-gray-50 dark:bg-purple-900/20 text-left">
                      <th className="p-2 font-semibold">Venda</th>
                      <th className="p-2 font-semibold">Tipo</th>
                      <th className="p-2 font-semibold text-center">Tentativas</th>
                      <th className="p-2 font-semibold">Próximo envio</th>
                      <th className="p-2 font-semibold">Situação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fila.map((i) => (
                      <tr key={i.id} className="border-b" data-testid={`sheets-fila-item-${i.id}`}>
                        <td className="p-2"><span className="font-mono text-xs">#{i.protocolo || '—'}</span><span className="block text-xs text-gray-500 truncate max-w-[220px]">{i.cliente}</span></td>
                        <td className="p-2 text-xs">{i.tipo === 'historico' ? `Histórico: ${i.payload?.status_antigo || '—'} → ${i.payload?.status_novo || ''}` : `Linha da venda (${i.venda_status || ''})`}</td>
                        <td className="p-2 text-center">{i.tentativas || 0}</td>
                        <td className="p-2 text-xs">{i.status === 'pendente' ? fmtDataHora(i.proximo_em) : '-'}</td>
                        <td className="p-2">
                          {i.status === 'erro' ? <Badge className="bg-red-100 text-red-700" title={i.erro || ''}>Erro</Badge> : <Badge className="bg-amber-100 text-amber-700">Pendente</Badge>}
                          {i.erro && <p className="text-[11px] text-red-600 mt-1 max-w-[260px] truncate" title={i.erro}>{i.erro}</p>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="sheets-colunas-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Colunas da aba "{status?.abas?.vendas}" (modelo enviado)</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-1.5">
          {(status?.colunas || []).map((c) => <Badge key={c} variant="outline" className="font-normal">{c}</Badge>)}
        </CardContent>
      </Card>
    </div>
  );
};

export default GoogleSheets;
